// lib/proto/content.ts — Bilingual content layer v3
//
// SSOT: docs/strategy/2026-07-08-copy-deck-v2.md (copy deck v2 — supersedes v1)
// ＋ docs/strategy/2026-07-11-copy-deck-v3.md (v2.1 → v3 delta — 2026-07-11
// Raychard 授權全面採用評審提案 35/35)
// ＋ docs/strategy/2026-07-12-copy-deck-v4.md (v3 → v4 delta — 核心語彙
// 「翻譯」→「折價／溢價」＋首頁減字，hands-off 授權，D-record 同檔).
// Sections referenced from v1 (§1.3 scenarios, §1.5 currencies, §2.3 modules,
// §4.2/§4.3) are those copy deck v2 explicitly carries forward ("同 v1").
//
// Language principle (AGENT-CONTRACT red line #2): zh-TW is the PRIMARY
// language, not a translation. en is a re-angle for international readability.
// Rendering rule (hybrid prototype): zh renders as primary text, en as a
// subtle secondary/kicker line.
//
// Hard rules baked in (copy deck v2 header):
//   ① first-screen proper nouns ≤3 (Raychard／品牌工程／RedPeak)
//   ② 「湧現」 is BANNED on the homepage — it lives only on /brand-engineering
//     and in essays. Strings containing it are marked METHOD-PAGE-ONLY below.
//
// Anonymization policy (docs/strategy/evidence/2026-07-08-verification-interview.md §5):
// cases publish as anonymous high-resolution; client names render ONLY after
// written consent. Named data lives in the consent-pending comment block at
// the bottom of this file.

export type Bi = { zh: string; en: string }

// ── Identity ──────────────────────────────────────────────────────────────────

export const identity = {
  name: "Raychard Huang",
  // Registry #4 resolved 2026-07-08. NOTE: not rendered on the hybrid homepage
  // (proper-noun budget); used by /about · /talks · schema.
  role: "Creative Intelligence Officer, RedPeak",
  // Hero 主標 — LOCKED (copy deck v2 §1.1)
  promise: {
    zh: "從複雜到信任，品牌、資本、科學。",
    en: "From complexity to trust: in brand, in capital, in science.",
  },
  // Hero 副線 — LOCKED (R-1, 2026-07-07 拍板)
  sub: {
    zh: "信任不是我做出來的。是我把條件擺對，讓它自己長出來。方法，叫品牌工程。",
    en: "I don't manufacture trust. I set the conditions for it to grow. The method: Brand Engineering.",
  },
  location: "Taipei, Taiwan",
  availability: "Selective engagements · 2026",
  // zh 主欄位版 — H14 (2026-07-11 v3 delta): 中文欄位不放英文.
  availabilityZh: "選擇性接案 · 2026",
  email: "hi@raychard.co",
  linkedin: "https://www.linkedin.com/in/hsinhsiangh/",
} as const

// Honest version marker shown when the hero convergence resolves
// (docs/plans/2026-07-08-hero-convergence-spec.md, behavior §2).
export const heroBadge = "resolved · copy v3 · 2026-07"

// Global CTAs (copy deck v1 §0.2 — carried by v2 §0; v3 delta 2026-07-11)
export const cta = {
  primary: { zh: "開始一段策略對話", en: "Start a strategic conversation" },
  // Header 膠囊專用短版 — 提案 §4.3/#35 (CtaBlock 與 hero 維持全句)
  primaryShort: { zh: "開始對話", en: "Start a conversation" },
  secondary: { zh: "先試 90 秒診斷", en: "Try the 90-second diagnostic" },
  tertiary: { zh: "訂閱每週一篇", en: "Subscribe to my thinking" }, // H15
} as const

// Footer line (copy deck v1 §0.3 — carried by v2 §0)
export const footerLine = {
  zh: "Raychard Huang · 從複雜到信任。委任案由 RedPeak 交付。",
  en: "Raychard Huang · from complexity to trust. Engagements delivered via RedPeak.",
} as const

// registry #8: partial — presented without client names
export const stats = [
  { value: "50+", label: { zh: "策略委任", en: "Strategic engagements" } },
  { value: "15+", label: { zh: "年，在品牌與市場的交界", en: "Years at the brand–market border" } },
  { value: "6", label: { zh: "產業領域", en: "Industry verticals" } },
] as const

// ── Problem statement — 第二屏 (copy deck v4 §1.2) ────────────────────────────
// v4 (2026-07-12, D-record: naming sprint): 核心語彙「翻譯」→「折價／溢價」。
// 原 L2「複雜的想法失敗…還沒被理解」刪除（與 L3 語義重疊；每幕恰一主張）。
// 最後一行是第二幕開場巨字——被折價，是買家自己的損益語言。

export const problem = {
  eyebrow: { zh: "問題", en: "The problem" },
  lines: [
    {
      // H1 (v3, 2026-07-11): 讓 hero 副線獨佔「信任不是」句式（裁定 D1）
      zh: "砸再多錢，買不到信任。它是市場自己算出來、自己決定給你的。",
      en: "Trust can't be bought. Markets calculate it and decide, on their own, whether you've earned it.",
    },
    {
      // H2 (v3): 三聯句分號改句號，每一刀單獨落下
      zh: "工程師做得出的東西，市場不一定買單。財報寫得出的數字，投資人不一定看得懂。科學證得出的證據，社會聽到的只剩恐慌。或者，冷漠。",
      en: "Markets miss value engineers can prove. Investors miss meaning numbers can't carry. The public hears solid science as panic. Or noise.",
    },
    {
      // v4 第二幕開場巨字（原「缺的不是更多資訊。是翻譯。」退役，D-record 2026-07-12）
      zh: "你的複雜，正在被市場折價。",
      en: "Your complexity is trading at a discount.",
    },
  ],
  // 第二幕開場的工作宣告＋利息帶（aha primitive c；結構性陳述，零虛構數字）
  repricing: {
    work: {
      zh: "我的工作：把折價，變回溢價。",
      en: "My work: turning that discount back into a premium.",
    },
    interest: {
      zh: "折價不會自己停。拖一季，付一季的利息。",
      en: "A discount doesn't stop itself. Every quarter you wait charges interest.",
    },
  },
} as const

// ── What I help with — 四情境 (copy deck v1 §1.3, carried by v2 §1.3) ─────────

export type Scenario = {
  id: string
  title: Bi
  // zh one-liner is the primary copy; en re-angles filled per v3 delta H5
  // (proposal §4.1, 2026-07-11 full adoption).
  line: Bi
}

export const scenarios: Scenario[] = [
  {
    id: "founder-vision",
    title: { zh: "創辦人願景 → 市場語言", en: "Founder vision → market language" },
    line: {
      zh: "你腦中已經成立的事，讓市場也聽得懂、記得住、願意買單。",
      en: "It's already settled in your head. Making the market hear it, remember it, and buy it: that's the work.",
    },
  },
  {
    id: "local-mastery",
    title: { zh: "台灣實力 → 世界舞台", en: "Local mastery → global resonance" },
    line: {
      // v4：去「翻譯」語彙，改折價框架
      zh: "在地打磨幾十年的實力，出了國門，不該被折價。",
      en: "Decades of craft, honed at home, shouldn't trade at a discount abroad.",
    },
  },
  {
    id: "going-public",
    title: { zh: "準上市公司 → 資本市場信任", en: "Going public → capital-market trust" },
    line: {
      zh: "興櫃到掛牌的每一步，敘事、證據與時機的系統性整備。",
      en: "Registration to listing: story, proof, and timing, prepared as one system.",
    },
  },
  {
    id: "hard-evidence",
    title: { zh: "複雜證據 → 公眾理解", en: "Hard evidence → public understanding" },
    line: {
      zh: "不販賣焦慮也不販賣安心，讓困難的科學被準確地理解。",
      en: "No selling fear, no selling comfort. Hard science, understood on its own terms.",
    },
  },
]

// ── The named method — Brand Engineering／品牌工程 ────────────────────────────
// The Codex-era framework-name placeholder is retired
// (docs/strategy/2026-07-07-integrated-positioning-and-site-plan.md).

export const framework = {
  name: { zh: "品牌工程", en: "Brand Engineering" },
  // Method summary card (copy deck v2 §1.4 — homepage-safe rewrite, no 湧現)
  // H3 (v3, 裁定 D2): 正典動詞鏈＝診斷 → 模擬 → 測試 → 迭代（「設計」→「模擬」）
  thesis: {
    zh: "品牌不是一句 slogan、一次 workshop、一本 brand book。品牌是可被診斷、模擬、測試、迭代的系統。信任，是系統跑對了，市場才給你的結果。",
    en: "A brand is not a slogan, a workshop, or a brand book. It is a system you can diagnose, simulate, test, and iterate. Trust is what the market pays you when the system runs true.",
  },
  cardCta: { zh: "看方法怎麼運作 →", en: "See how the method works →" },

  // ⚠ METHOD-PAGE-ONLY (/brand-engineering). The zh one-liners below contain
  // 「湧現」, which is BANNED on the homepage (copy deck v2 hard rule ②).
  // Never render `framework.modules` in any homepage/hybrid section.
  modules: [
    {
      id: "brand-simulation",
      // B3 (v3): 中英名對齊 — zh 改「品牌系統模擬」
      name: { zh: "品牌系統模擬", en: "Brand Simulation" },
      line: {
        zh: "設計並模擬你的敘事、證據、受眾、市場力量與時間節奏，如何在真實環境中互相作用。",
        en: "Design and simulate how your story, proof, audience, market forces, and timing interact under real-world conditions.",
      },
    },
    {
      id: "narrative-stress-test",
      name: { zh: "敘事壓力測試", en: "Narrative Stress Test" },
      line: {
        zh: "你的故事撐得住懷疑的投資人、挑剔的工程師、盡職調查與法說會嗎？先在房間裡試壓，再上場。",
        en: "Will your story survive a skeptical investor, a picky engineer, due diligence, an earnings call? Pressure-test it in the room before the market does.",
      },
    },
    {
      id: "trust-system-mapping",
      name: { zh: "信任系統地圖", en: "Trust System Mapping" },
      line: {
        // ⚠ contains 湧現 — method page only
        zh: "信任從哪些接觸與互動湧現、在哪裡斷裂，把它畫出來，才修得了。",
        en: "Map where trust actually emerges across touchpoints, and where it breaks. You can't fix what you can't see.",
      },
    },
    {
      id: "coach-system",
      name: { zh: "陪跑教練＋系統", en: "Coach + System" },
      line: {
        zh: "不是只給建議，也不是只給工具，而是陪你把品牌系統跑起來。",
        en: "Not advice alone, not tools alone, but a coach and a system, running your brand together in real market conditions.",
      },
    },
  ],

  // Legacy fields — used ONLY by the retired A/B/C prototype routes.
  engine: "Creative Intelligence",
  arc: ["Complexity", "Translation", "Trust"] as readonly string[],
} as const

// ── Three currencies — 三支柱入口 (copy deck v1 §1.5; subtitles LOCKED) ───────

export type Currency = {
  id: "meaning" | "value" | "truth"
  name: Bi
  subtitle: Bi // zh LOCKED; en is a Builder re-angle draft, Editor to confirm
  line: Bi // zh DRAFT from deck; en re-angles filled per v3 delta H6 (§4.2)
}

export const currencies = {
  title: { zh: "市場支付信任的三種貨幣", en: "The three currencies of trust" },
  pillars: [
    {
      id: "meaning",
      name: { zh: "意義", en: "Meaning" },
      subtitle: { zh: "深科技品牌策略", en: "Deep-tech brand strategy" },
      line: {
        zh: "讓做得出來的技術，變成被相信的品牌。",
        en: "Technology that works, turned into a brand that's believed.",
      },
    },
    {
      id: "value",
      name: { zh: "價值", en: "Value" },
      subtitle: { zh: "資本市場整備／興櫃上市之路", en: "Capital-market readiness" },
      line: {
        zh: "上市不是終點，是一次公開的期中考，信任提早準備。",
        en: "Listing day is a public midterm. Trust is prepared quarters ahead.",
      },
    },
    {
      id: "truth",
      name: { zh: "真相", en: "Truth" },
      subtitle: { zh: "證據等級的永續科學傳播", en: "Evidence-graded science communication" },
      line: {
        zh: "對複雜的科學誠實，是最稀缺的品牌動作。",
        en: "Honesty about complex science: the rarest move a brand can make.",
      },
    },
  ] as Currency[],
} as const

// ── Diagnostic entrance — homepage copy (copy deck v1 §1.6 ＋ v2 §1.6 追加句) ──
// The instrument's internal lexicon stays in `diagnostic` below; the /diagnose
// comparative-scoring rework is a separate lane (expert panel §4) — not here.

export const diagnosticEntry = {
  kicker: { zh: "90 秒診斷", en: "The 90-second diagnostic" },
  // v4：標題接上折價語彙（診斷所測的「定位卡住」＝折價發生的位置）
  title: {
    zh: "90 秒，看你在哪裡被折價",
    en: "90 seconds to find where you're being discounted.",
  },
  // H8 (v3, 裁定 D5) → v4 減字：儀器本體已有完整說明，入口只留交換條件一句
  intro: {
    zh: "跑完想要完整報告？留下 email，我寄給你。",
    en: "Want the full report afterward? Leave an email and it's yours.",
  },
} as const

// ── Rewrite demo — aha primitive (a)，第二幕開場後的 before→after 改寫實例 ────
// 樣本文字為說明而寫（同 diagnostic-examples #1 的 input），非真實客戶資料。
// 「after」是示範改寫：形容詞退場，換上可驗證的名詞——手藝的瞬間。

export const rewriteDemo = {
  before:
    "我們是領先業界的智慧解決方案供應商，以創新技術與世界級品質，提供端到端的次世代產品，賦能客戶的數位轉型。",
  after: {
    zh: "我們做車規級儲存模組，讓自駕系統在斷電的瞬間，也不丟資料。",
    en: "We build automotive-grade storage modules, so an autonomous system loses power, not data.",
  },
  note: {
    zh: "差別不是文筆，是決策：形容詞退場，換上可驗證的名詞。示範樣本，非客戶資料。",
    en: "The difference isn't prose. It's deciding: adjectives out, verifiable nouns in. Demonstration sample, not client data.",
  },
  labels: { before: "被折價的寫法", after: "拿回溢價的寫法" },
} as const

// ── Cases — anonymized, high-resolution ───────────────────────────────────────
// Policy: anonymous until written consent (verification interview §5).
// zh narrative drafts by Builder from the verified en source data — Editor to
// review wording; facts unchanged.

export type EvidenceTier = "Public" | "Named outcome" | "Reference on request"

export type CaseStudyV2 = {
  id: string
  client: Bi // descriptive anonym — real names in the consent-pending block below
  industry: Bi
  frame: Bi // the strategic reframe this case enacts
  shift: Bi
  tension: Bi
  move: Bi
  outcome: Bi
  metrics: { label: Bi; value: string }[]
  evidence: EvidenceTier
  testimonial?: { quote: string; position: string } // name withheld pending consent
  accent: string
}

export const cases: CaseStudyV2[] = [
  {
    id: "edge-ai-storage",
    client: {
      zh: "工業級儲存與 Edge AI 領導廠商",
      en: "An edge-AI industrial-storage leader",
    },
    industry: { zh: "Edge AI · 工業級儲存", en: "Edge AI · Industrial storage" },
    frame: { zh: "技術 → 人性", en: "Technology → Humanity" },
    shift: {
      zh: "從零組件供應商，到 Edge AI 生態系的架構者。",
      en: "From component vendor to AI-ecosystem architect.",
    },
    tension: {
      zh: "有真實的 Edge AI 實力、有一線平台夥伴關係，市場卻只當它是「另一家 SSD 廠」，大宗商品的印象壓住每一筆交易。",
      en: "Read as 'another SSD manufacturer' despite real edge-AI capability and top-tier platform partnerships, commodity perception capping every deal.",
    },
    move: {
      zh: "以「架構智慧」為核心敘事重建每一個接觸點，把儲存重新定位為 Edge AI 系統的智慧層。",
      en: "Built an 'architecting intelligence' narrative across every touchpoint, repositioning storage as the intelligence layer of edge-AI systems.",
    },
    outcome: {
      // C1 (v3): 開頭斷裂修句，事實零改動
      zh: "這套敘事被全公司採用。2024 年取得兩大 AI 運算平台的最高階夥伴資格，營收創新高；銷售對話從價格轉向架構。",
      en: "Adopted company-wide; reached the top partner tier of two leading AI-compute platforms in 2024 with record revenue. Sales conversations moved from price to architecture.",
    },
    metrics: [
      { label: { zh: "營收年成長", en: "Revenue growth YoY" }, value: "Accelerated" },
      { label: { zh: "品牌認知轉變", en: "Brand-perception shift" }, value: "Elevated" },
      { label: { zh: "銷售週期縮短", en: "Sales-cycle reduction" }, value: "Shortened" },
      { label: { zh: "夥伴層級", en: "Partner tier" }, value: "Top" },
    ],
    evidence: "Reference on request", // was "Named outcome" — downgraded pending consent
    testimonial: {
      quote:
        "Raychard helped us see what we couldn't articulate ourselves. We're not selling storage. We're architecting intelligence at the edge. That clarity transformed our market position.",
      position: "Company leadership", // generalized: real signatory is founder-level, not a marketing VP; name withheld pending consent
    },
    accent: "#C9A227",
  },
  {
    id: "fluid-equipment",
    client: {
      zh: "五十年歷史的流體設備製造商",
      en: "A 50-year fluid-equipment manufacturer",
    },
    industry: { zh: "工業設備 · 流體管理", en: "Industrial equipment · Fluid management" },
    frame: { zh: "傳承 → 未來", en: "Legacy → Future" },
    shift: {
      zh: "從五十年的泵浦製造者，到智慧製造的領導者。",
      en: "From 50-year pump maker to intelligent-manufacturing leader.",
    },
    tension: {
      zh: "半世紀的工藝被讀成「老」。沒有品牌系統，決策各行其是，識別稀釋，也難以吸引下一代人才。",
      en: "Half a century of mastery read as 'old'. No brand system meant inconsistent decisions, diluted identity, and difficulty attracting the next generation.",
    },
    move: {
      zh: "設計一整套品牌作業系統：定位、架構、訊息、治理，讓傳承接上未來。",
      en: "Designed a full brand operating system: positioning, architecture, messaging, and governance, connecting heritage to what comes next.",
    },
    outcome: {
      zh: "品牌營運變成可擴張的系統；吸引到更年輕的工程人才；在傳統產業裡站上數位轉型領導者的位置。",
      en: "Brand operations transformed into a scalable system; younger engineering talent attracted; positioned as a digital-transformation leader in a traditional sector.",
    },
    metrics: [
      { label: { zh: "上市時程縮短", en: "Time-to-market" }, value: "Accelerated" },
      { label: { zh: "系統模組", en: "System modules" }, value: "5" },
      { label: { zh: "品牌手冊", en: "Brand playbooks" }, value: "8" },
      { label: { zh: "內部評分", en: "Stakeholder rating" }, value: "4.8/5" },
    ],
    evidence: "Reference on request", // was "Named outcome" — downgraded pending consent
    testimonial: {
      quote:
        "Raychard built us a complete operating system for our brand. It transformed how we work and how the market sees us.",
      position: "Brand Director", // name withheld pending consent
    },
    accent: "#B06A3B",
  },
  {
    id: "test-measurement",
    client: { zh: "量測設備領導廠商", en: "A test-and-measurement leader" },
    industry: { zh: "精密量測", en: "Precision test & measurement" },
    frame: { zh: "數據 → 敘事", en: "Data → Story" },
    shift: {
      zh: "從規格表，到高階決策者會為之行動的敘事。",
      en: "From spec sheets to narratives executives act on.",
    },
    tension: {
      zh: "技術實力備受肯定，價值卻只能用規格說。工程師讀得懂數字，決策者需要的是故事。",
      en: "Recognized for technical excellence but unable to communicate value beyond specifications. Engineers understood the numbers, decision-makers needed a story.",
    },
    move: {
      // v4：去「翻譯」動詞（事實零改動）
      zh: "把公司定位成客戶每一次突破背後的推手：把精密，變成決策者讀得懂的商業價值。",
      en: "Positioned the company as the enabler behind its customers' breakthroughs, turning precision into business value executives can read.",
    },
    outcome: {
      // C2 (v3): 去被動式
      zh: "在企業決策層與高階主管眼中，位置重新拉高；在同質化的市場裡，以成果敘事做出差異。",
      en: "Elevated perception among enterprise decision-makers and the C-suite; differentiated in a commoditized market through outcome storytelling.",
    },
    metrics: [
      { label: { zh: "企業端互動", en: "Enterprise engagement" }, value: "Elevated" },
      { label: { zh: "品牌認知", en: "Brand perception" }, value: "Strengthened" },
      { label: { zh: "市場區隔", en: "Market segments" }, value: "12+" },
      { label: { zh: "溢價定位", en: "Premium position" }, value: "Achieved" },
    ],
    evidence: "Reference on request",
    accent: "#2A9D8F",
  },
  {
    id: "global-semiconductor",
    client: { zh: "全球半導體公司", en: "A global semiconductor company" },
    industry: { zh: "半導體", en: "Semiconductor" },
    frame: { zh: "認知 → 現實", en: "Perception → Reality" },
    shift: {
      zh: "從「平價替代品」，到全球工程師的首選雇主。",
      en: "From 'budget alternative' to employer of choice for global engineers.",
    },
    tension: {
      zh: "明明是全球半導體領導者，卻仍被讀成平價晶片品牌，頂尖工程人才流向名氣更大的對手。",
      en: "A global semiconductor leader still read as a budget chipset brand, losing top engineering talent to better-known names in the US and India.",
    },
    move: {
      zh: "從 40 多場員工訪談建出差異化的雇主價值主張：讓先進技術為數十億人所用。",
      en: "Built a differentiated employer value proposition from 40+ employee interviews, positioning the company as democratizing advanced technology for billions.",
    },
    outcome: {
      zh: "在關鍵人才市場拉升品牌認知與應徵率；雇主價值主張現已貫穿招募與內部溝通全線。",
      en: "Campaigns lifted awareness and application rates in key talent markets; the EVP now runs across career sites, recruiting, and internal comms.",
    },
    metrics: [
      { label: { zh: "品牌認知提升", en: "Brand-awareness lift" }, value: "Elevated" },
      { label: { zh: "應徵率", en: "Application rate" }, value: "Increased" },
      { label: { zh: "員工訪談", en: "Employee interviews" }, value: "40+" },
      { label: { zh: "目標市場", en: "Target markets" }, value: "2" },
    ],
    evidence: "Reference on request",
    accent: "#8392A4",
  },
]

// Rendered footnote under the cases grid — honesty microcopy for the
// tiered-anonymity model (DRAFT, deck §9 register).
export const casesNote = {
  zh: "案例依保密政策匿名呈現。守得住客戶的機密，才守得住客戶的信任。",
  en: "Cases are shown anonymously, by policy. Keeping a client's confidence is part of keeping a client's trust.",
} as const

// Tiered-anonymity model — confidentiality as a solved operating model.
export const evidenceModel = [
  { tier: "Public deltas", detail: "Named outcomes a client has cleared for public reference." },
  { tier: "Gated diagrams", detail: "Method artifacts and frameworks, shared under NDA on request." },
  { tier: "Private walkthroughs", detail: "Full case detail, method and results, walked through directly under NDA." },
] as const

// ── About (copy deck v2 §4) ───────────────────────────────────────────────────

export const about = {
  // §4.1 背景線 — LOCKED (2026-07-08 查證解鎖，無年份版)
  background: {
    zh: "十五年來，我都在品牌與市場的交界工作，先是幫國際品牌進入亞洲，後來調轉方向，幫台灣的深科技公司走向世界。",
    en: "For fifteen years I've worked at the border between brands and markets: first helping international brands enter Asia, then turning the other way to help Taiwan's deep-tech companies go global.",
  },
  // §4.2 定位段 — LOCKED（deck 本體不動）
  positioning: {
    zh: "一間 AI 原生的策略事務所：把技術的複雜，轉譯成掙來的信任，為深科技品牌、為走向上市的公司、為大眾需要讀懂的科學。",
    en: "An AI-native strategy practice turning technical complexity into earned trust, for deep-tech brands, for companies going public, for science the public needs to understand.",
  },
  // §4.2 /about 渲染版 — A1 主詞修正前導半句（裁定 D7）
  // v1.1 (2026-07-11 Raychard 拍板 via full-adoption)：僅 /about 頁使用；
  // 首頁 teaser 與 deck 定位段本體維持上方 LOCKED 版。
  positioningAboutPage: {
    zh: "我把這份工作，做成了一間 AI 原生的策略事務所：把技術的複雜，轉譯成掙來的信任，為深科技品牌、為走向上市的公司、為大眾需要讀懂的科學。",
    en: "I've built this work into an AI-native strategy practice: turning technical complexity into earned trust for deep-tech brands, for companies going public, for science the public needs to understand.",
  },
  // §4.3 Worldview 三問 — DRAFT (carried from v1)
  worldview: {
    intro: {
      zh: "工作之下，我長期帶著三個問題：",
      en: "Beneath the work, I carry three long-term questions:",
    },
    questions: [
      {
        name: { zh: "創造", en: "Creation" },
        line: {
          zh: "AI 時代，人的創造力怎麼被放大，而不是被取代？",
          en: "How human creativity gets amplified, not replaced, in the age of AI.",
        },
      },
      {
        name: { zh: "永續", en: "Sustainability" },
        line: {
          zh: "我們做的事，能不能讓人活得更好，也對環境更好？",
          en: "Whether what we build leaves people healthier, happier, and the planet better.",
        },
      },
      {
        name: { zh: "價值", en: "Value" },
        line: {
          zh: "當機器能做的越來越多，人的價值是什麼？",
          en: "What human work and life are worth when machines can do more and more.",
        },
      },
    ],
    outro: {
      zh: "我沒有單一的答案。但長期站在這三個問題旁邊，讓我在它們交界的地方，辨認出最重要的元素。",
      en: "I don't pretend to have a single answer. But staying beside these questions is how I recognize what matters most when it appears.",
    },
  },
} as const

// ── Pull-quote pool (copy deck v2 §10) ────────────────────────────────────────

export type PullQuote = {
  zh: string
  en?: string
  // "method-page-only" quotes contain 「湧現」 — /brand-engineering & essays
  // ONLY; never render on the homepage.
  scope: "site" | "method-page-only"
}

export const pullQuotes: PullQuote[] = [
  {
    zh: "信任不打折，只複利。",
    en: "Trust doesn't discount. It compounds.",
    scope: "site",
  },
  {
    // v4：原「翻譯不是換句話說」退役（核心語彙更換，D-record 2026-07-12）
    zh: "市場給你的折價，是還沒說清楚的部分，在收利息。",
    en: "The market's discount is interest charged on what you haven't yet made clear.",
    scope: "site",
  },
  {
    zh: "上市那天，市場不是在看你的財報，是在看你敢不敢被看穿。",
    en: "On listing day, the market isn't reading your numbers. It's checking whether you can survive being seen through.",
    scope: "site",
  },
  {
    zh: "證據分等級，誠實不分等級。",
    en: "Evidence has grades. Honesty doesn't.",
    scope: "site",
  },
  {
    // ⚠ contains 湧現 — essays/method page only (deck §10 #5)
    zh: "湧現不是奇蹟，是你把該擺的東西都擺對了，市場才肯給你的獎勵。",
    scope: "method-page-only",
  },
  {
    // ⚠ contains 湧現 — method page only, LOCKED (deck §10 #6 / v1 §2.2 金句)
    zh: "品牌不是元素清單，而是一個能讓信任湧現的場。",
    en: "A brand is not a list of elements. It is a field where trust can emerge.",
    scope: "method-page-only",
  },
]

// ── Subscribe & offers — data stubs, placeholders tracked ─────────────────────

export const subscribe = {
  // LOCKED 2026-07-09 (copy deck v2 §8.1, Raychard 拍板)
  name: { zh: "《品牌工程・實戰筆記》", en: "Brand Engineering, In Practice" },
  pitch: {
    zh: "每週一篇，關於品牌、資本與科學裡的信任如何被建造。不談祕訣，談判斷。",
    en: "One essay a week on how trust is engineered: in brand, in capital, in science. No hacks. Judgment.",
  },
} as const

// ── /brand-engineering method page (copy deck v2 §2; v1 §2 carried) ──────────
// ⚠ METHOD-PAGE-ONLY: several strings below contain 「湧現」 — legal here and
// in essays ONLY (hard rule ②). Never import this block into homepage code.

export const methodPage = {
  // §2.1 定義 — versioned per spec §2.2 — evolveHistory 連結.
  // v1.1 (2026-07-11 Raychard 拍板 via full-adoption)：B1 拆句版，語義零改動，
  // 過硬規則③（>25 字不靠「而是」撐邏輯）。v1.0 見 copy deck v2 §2.1。
  definition: {
    version: "v1.1",
    zh: "品牌工程：一套以系統觀為基礎的方法，協助企業在動態市場中被理解、被信任、被選擇。它不控制品牌結果。它設計條件，讓理解、信任與共鳴，自己湧現。",
    en: "Brand Engineering is a systems-based methodology for helping companies become understood, trusted, and chosen in dynamic markets. It doesn't control brand outcomes. It designs the conditions for understanding, trust, and resonance to emerge.",
  },
  // §2.2 為什麼品牌是系統 — v3 delta B2 (裁定 D3)：羽毛句拆短修利，
  // 「會飛的是鳥——是排列，不是羽毛」；過硬規則③。
  whySystem: {
    zh: [
      "沒有一個元素能單獨造出信任。敘事、證據、時機、行為，湊在一起才長得出來。沒有一片羽毛會飛。會飛的是鳥，是排列，不是羽毛。",
      "學術上這叫湧現（emergence）。白話講：信任不是做出來的，是湊出來的。你可以設計條件，不能下指令要求它發生。",
      "所以我不做「一頁定位聲明」就離場的顧問。我把品牌當成活系統：診斷它、模擬它、在真實市場回饋中迭代它。",
    ],
    en: [
      "No single element produces trust. It grows where story, proof, timing, and behavior interact. No feather can fly. A bird can: the arrangement, not the feathers.",
      "The academic word is emergence. The plain version: trust isn't made, it's assembled. You can design the conditions; you cannot order it to happen.",
      "That's why I don't hand over a positioning statement and leave. The brand is a living system: diagnose it, simulate it, iterate it against real market feedback.",
    ],
    // 白話錨定 highlight inside paragraph 2 (rendered emphasized)
    anchor: { zh: "信任不是做出來的，是湊出來的。", en: "Trust isn't made, it's assembled." },
  },
  // §2.4 Builder/Maker × AI-native — DRAFT (v2 rewrite: judgment over automation)
  aiNative: {
    zh: [
      "好的策略不該停在簡報裡。它得走出來，自己站起來，自己動。",
      "我是動手做的策略者。我的工作方式由一套多代理 AI 系統支撐，但賣點從來不是「我用了 AI」。是我指揮 AI 時施加的判斷：哪裡該快、哪裡該慢、哪裡該推翻重來。品味與否決權，永遠是人的。",
    ],
    en: [
      "Good strategy shouldn't die in a deck. It has to walk out, stand up, and run.",
      "My practice runs on a multi-agent AI system, but the point was never \"I use AI.\" The point is the judgment applied while directing it: where to accelerate, where to slow down, where to throw it away and start over. Taste and veto stay human.",
    ],
    emphasis: {
      zh: "品味與否決權，永遠是人的。",
      en: "Taste and veto stay human.",
    },
  },
  // §2.5 頁尾 CTA — v3 delta B6：去三行內重複句，補 en
  closing: {
    zh: "想看這套方法用在你身上？先做 90 秒診斷。或者，直接來談。",
    en: "Want this method on your problem? Run the 90-second diagnostic, or just start the conversation.",
  },
} as const

// ── /work-with-me offer ladder (copy deck v2 §6 — revised ladder) ────────────
// Pricing policy LOCKED (2026-07-08 Raychard 拍板): NO prices anywhere on the
// site — "報價於對話中提供".

export const workWithMe = {
  // W1 (v3)：開場末句去翻譯腔
  opening: {
    zh: "不是每個問題都需要委任。先讀文章、先做診斷，都是好起點。當你需要的是有人陪你上場，從這裡開始。",
    en: "Not every problem needs an engagement. The essays and the diagnostic are good places to start. When you need someone in the room with you, start here.",
  },
  offers: [
    {
      id: "diagnostic",
      tier: { zh: "入口・免費", en: "Entry · Free" },
      name: { zh: "90 秒診斷", en: "The 90-second diagnostic" },
      // W2 (v3, 裁定 D5)：「不留資料」精確化——邊界講清楚
      line: {
        zh: "貼上你的公司介紹或 About 頁，看你的定位卡在哪。AI、自助；你貼的文字，不儲存。",
        en: "Paste your About page and see where your positioning is stuck. AI-run, self-serve; the text you paste is never stored.",
      },
      href: "/brand-engineering#diagnostic",
      flagship: false,
    },
    {
      id: "positioning-audit",
      // W3 (v3, 裁定 D6)：去 offer ladder 內部樓層名與工程語
      tier: { zh: "固定費制・不用開會", en: "Fixed fee" },
      name: { zh: "Positioning Audit 定位健檢", en: "Positioning Audit" },
      line: {
        zh: "我親自讀你所有對外素材，一週內交書面評估，附 Sprint 該做多大的建議。",
        en: "I read your public-facing materials myself and return a written assessment within a week, plus how big a Sprint you actually need.",
      },
      flagship: false,
    },
    {
      id: "sprint",
      tier: { zh: "旗艦委任", en: "Flagship" },
      name: { zh: "Brand Engineering Sprint", en: "Brand Engineering Sprint" },
      line: {
        zh: "診斷 → 模擬 → 敘事系統 → 陪跑迭代。固定流程、固定產出：一套可運作的品牌系統，不是一疊簡報。",
        en: "Diagnose → simulate → narrative system → iterate together. Fixed process, fixed output: an operating brand system, not a stack of slides.",
      },
      flagship: true,
    },
    {
      id: "ipo-readiness",
      tier: { zh: "旗艦委任・價值支柱", en: "Flagship" },
      name: { zh: "IPO 敘事整備", en: "IPO narrative readiness" },
      line: {
        zh: "從興櫃到掛牌的資本市場信任系統：公開說明書敘事、法說會壓力測試、投資人溝通節奏。",
        en: "A capital-market trust system from registration to listing: prospectus narrative, earnings-call stress tests, investor-communication cadence.",
      },
      flagship: true,
    },
    {
      id: "sparring",
      tier: { zh: "側翼・付費制", en: "Paid session" },
      name: { zh: "Founder / Executive Sparring", en: "Founder / Executive Sparring" },
      line: {
        zh: "一小時，帶你最難的敘事問題來。不客套，直接打。費用可折抵後續 Sprint。",
        en: "One hour. Bring your hardest narrative problem. No pleasantries. The fee credits toward a later Sprint.",
      },
      flagship: false,
    },
    {
      id: "speaking",
      tier: { zh: "側翼", en: "Speaking" },
      name: { zh: "Speaking / Workshops", en: "Speaking / Workshops" },
      line: {
        zh: "品牌工程、複雜→信任、AI 時代的品牌打法。中英雙語授課。",
        en: "Brand Engineering, complexity→trust, brand strategy in the AI era. Delivered in Chinese or English.",
      },
      flagship: false,
    },
  ],
  // W4 (v3)：政策不動（LOCKED），補「為什麼」把政策變立場
  pricingNote: {
    zh: "本站不列價格，先談清楚範疇，報價才有意義。固定費制與委任報價，於對話中提供。",
    en: "No prices on this site: a quote only means something after scope. Fixed fees and engagement quotes are given in conversation.",
  },
  // 收尾 LOCKED + link to /about#continuity (§4.5)
  closing: {
    zh: "所有委任由 RedPeak 團隊交付。",
    en: "All engagements are delivered by the RedPeak team.",
  },
} as const

// ── Five gaps — legacy diagnostic vocabulary (A/B/C prototypes + scan) ────────
// Client tags anonymized per policy; named originals in the consent-pending
// block at the bottom of this file.

export type Gap = {
  id: string
  from: string
  to: string
  line: string
  clients: string[]
}

export const gaps: Gap[] = [
  {
    id: "tech-humanity",
    from: "Technology",
    to: "Humanity",
    line: "Engineering the market can't feel is engineering the market won't pay for.",
    clients: ["Edge-AI storage leader", "Global semiconductor company", "Edge-AI compute maker"],
  },
  {
    id: "data-story",
    from: "Data",
    to: "Story",
    line: "Behind every spec is a reason someone should care. Most decks bury it.",
    clients: ["Test & measurement leader", "Precision machining maker"],
  },
  {
    id: "legacy-future",
    from: "Legacy",
    to: "Future",
    line: "Decades of mastery read as 'old' until you reframe heritage as proof.",
    clients: ["50-year fluid-equipment maker", "Global ODM", "Machine-tool builder"],
  },
  {
    id: "local-global",
    from: "Local",
    to: "Global",
    line: "Asian technical leaders go unheard abroad for want of one clear voice.",
    clients: ["Pharma manufacturer", "Global energy company"],
  },
  {
    id: "perception-reality",
    from: "Perception",
    to: "Reality",
    line: "The gap between what you are and what they see is where deals are lost.",
    clients: ["Global semiconductor company", "Industrial-computing leader"],
  },
]

// ── Creative Intelligence engine — legacy (A/B/C prototypes only) ─────────────

export const engineRoles = [
  {
    role: "Intake",
    kind: "human",
    detail: "Dialogue, not questionnaires. I listen for the truth you can't yet say.",
  },
  {
    role: "Framing",
    kind: "human",
    detail: "Which of the five Translation Gaps you're actually stuck in.",
  },
  {
    role: "Exploration",
    kind: "agents",
    detail: "MaestrOS agents attack the problem from every angle, at machine scale.",
  },
  {
    role: "Translation",
    kind: "human",
    detail: "Human judgment finds the one line that connects. AI never decides this.",
  },
  {
    role: "Compounding",
    kind: "system",
    detail: "Every engagement makes the system sharper. You inherit all of it.",
  },
] as const

export const engineNote =
  "AI provides scale. A human sets direction. The machine never speaks to your market without judgment in the loop. That is the whole point."

// ── Point of view — legacy essay teasers (A/B/C prototypes only) ──────────────
// Not deck copy; retired from the hybrid homepage. Real essays ship via the
// content engine lane.

export const pov = [
  {
    title: "Your jargon is a confession.",
    dek: "When a deep-tech company hides behind 'innovative end-to-end solutions,' it's admitting it hasn't decided what it means. Specificity is the bravery the market rewards.",
    series: "On Translation",
    readingTime: "4 min",
  },
  {
    title: "AI doesn't replace the strategist. It replaces the junior team.",
    dek: "The unfair advantage isn't a chatbot. It's one senior judgment with the exploratory range of a 20-person studio, and none of the telephone game.",
    series: "On Creative Intelligence",
    readingTime: "6 min",
  },
  {
    title: "Heritage is not a liability. It's unspent proof.",
    dek: "Forty years of mastery reads as 'old' only until someone reframes it. The reframing is the work.",
    series: "On Translation",
    readingTime: "5 min",
  },
  {
    title: "Engineers buy clarity, not adjectives.",
    dek: "The most conservative technical buyer in the room is moved by exactly one thing: a claim they can verify. Build the page for them.",
    series: "On Deep-Tech B2B",
    readingTime: "5 min",
  },
] as const

// ── The scan instrument lexicon (component logic untouched) ───────────────────
// The /diagnose comparative-scoring rework (expert panel §4) is a separate
// lane; only the retired framework-name vocabulary was replaced here
// (→ Brand Engineering).
// H9 (v3, 裁定 D4)：介面 microcopy 全套 zh 化——zh-TW 主體原則不得在轉換率
// 最高的元件上失守。掃描引擎（translation-scan.ts）不動，比較式評分照原 lane。

export const diagnostic = {
  title: "定位診斷 · 90 秒",
  // kicker 僅剩 retired /prototype/practice 使用——H9 提案包未含，維持原字串
  kicker: "Run my method on your own words",
  intro:
    "貼上你的公司介紹、About 頁或一段對外文案（中英皆可）。90 秒，看你的定位卡在哪。",
  placeholder: "貼上你的 hero 一句話，或 About 段落⋯⋯",
  cta: "跑診斷",
  rerun: "換一段再跑",
  error: "再多貼一點，一句完整的 hero，或兩三句就夠。",
  sampleLink: "或試一段示範文案",
  // 掃描四狀態（沿 v1 §5）——元件的動態刪節號接在字尾
  stages: [
    "系統正在讀你的敘事",
    "檢測複雜度陷阱",
    "定位你卡住的地方",
    "生成品牌張力句",
  ],
  outro: "這是我工作方式的前五分鐘。想看接下來的部分，開始一段策略對話。",
  outroCta: "開始一段策略對話",
  // Buzzwords flagged as the "complexity trap" — found verbatim in the input.
  buzzwords: [
    "solutions",
    "innovative",
    "innovation",
    "leading",
    "leader",
    "cutting-edge",
    "cutting edge",
    "synergy",
    "leverage",
    "leveraging",
    "robust",
    "scalable",
    "next-generation",
    "next generation",
    "world-class",
    "world class",
    "seamless",
    "seamlessly",
    "holistic",
    "empower",
    "empowering",
    "end-to-end",
    "best-in-class",
    "best in class",
    "paradigm",
    "disruptive",
    "ecosystem",
    "optimize",
    "optimized",
    "state-of-the-art",
    "state of the art",
    "revolutionary",
    "transform",
    "transformative",
    "value-added",
    "turnkey",
    "mission-critical",
    "future-proof",
    "game-changing",
    "unparalleled",
    "industry-leading",
    "comprehensive",
    "dynamic",
    "agile",
    "smart",
    "advanced",
  ],
  // Keyword → Gap mapping for gap detection over the input.
  gapSignals: {
    "data-story": ["data", "measurement", "precision", "test", "spec", "metric", "accuracy", "instrument", "performance", "benchmark"],
    "legacy-future": ["years", "heritage", "founded", "tradition", "legacy", "established", "decades", "history", "since", "generation"],
    "tech-humanity": ["ai", "edge", "algorithm", "platform", "compute", "semiconductor", "technology", "intelligent", "chip", "software"],
    "local-global": ["asia", "taiwan", "global", "international", "worldwide", "export", "markets", "regional", "local"],
    "perception-reality": ["leader", "trusted", "quality", "reliable", "partner", "leading", "proven"],
  } as Record<string, string[]>,
  // Tension-statement templates per gap (filled with a detected subject noun).
  tensionTemplates: {
    "data-story": "The market reads your numbers. It does not yet feel why they matter.",
    "legacy-future": "Your decades of mastery are being mistaken for the past, not proof of the future.",
    "tech-humanity": "Your technology is real. The human reason to care is buried under it.",
    "local-global": "You lead at home and go unheard abroad, for want of one clear voice.",
    "perception-reality": "What you are and what the market sees have drifted apart. That gap is costing deals.",
  } as Record<string, string>,
} as const

// ─────────────────────────────────────────────────────────────────────────────
// LEGACY ADAPTERS — consumed ONLY by the retired A/B/C prototype routes
// (/prototype/resolve, /prototype/query, /prototype/practice) so they keep
// compiling. English-only projections of the bilingual v2 data above.
// Do NOT use these in /prototype/hybrid or on any v3 surface.
// ─────────────────────────────────────────────────────────────────────────────

export const identityEn = {
  name: identity.name,
  role: identity.role,
  promise: identity.promise.en,
  sub: identity.sub.en,
  location: identity.location,
  availability: identity.availability,
  email: identity.email,
  linkedin: identity.linkedin,
} as const

export const statsEn = stats.map((s) => ({ value: s.value, label: s.label.en }))

export const frameworkEn = {
  name: framework.name.en,
  engine: framework.engine,
  thesis: framework.thesis.en,
  arc: framework.arc,
} as const

// Old case shape kept for the retired prototypes.
export type CaseStudy = {
  id: string
  client: string
  industry: string
  gap: string
  shift: string
  tension: string
  move: string
  outcome: string
  metrics: { label: string; value: string }[]
  evidence: EvidenceTier
  testimonial?: { quote: string; author: string; position: string }
  accent: string
}

export const casesEn: CaseStudy[] = cases.map((c) => ({
  id: c.id,
  client: c.client.en,
  industry: c.industry.en,
  gap: c.frame.en,
  shift: c.shift.en,
  tension: c.tension.en,
  move: c.move.en,
  outcome: c.outcome.en,
  metrics: c.metrics.map((m) => ({ label: m.label.en, value: m.value })),
  evidence: c.evidence,
  testimonial: c.testimonial
    ? {
        quote: c.testimonial.quote,
        author: "Name withheld (pending consent)",
        position: c.testimonial.position,
      }
    : undefined,
  accent: c.accent,
}))

export const bio = {
  short: about.positioning.en,
  long: [
    about.background.en,
    `${about.worldview.intro.en} ${about.worldview.questions
      .map((q) => q.line.en)
      .join(" ")} ${about.worldview.outro.en}`,
  ],
} as const

// Named client wall — retired from rendering pending consent. The retired
// /prototype/query route maps over this; it stays empty until consent lands.
export const clients: readonly string[] = []

/*
 * [PENDING CLIENT CONSENT — do not render]
 * ---------------------------------------------------------------------------
 * Named data preserved for the day written consent arrives. Action items
 * tracked in docs/strategy/evidence/unverified-registry.md; policy set in
 * docs/strategy/evidence/2026-07-08-verification-interview.md §5.
 *
 * clients (former wall): MediaTek, Advantech, Innodisk, Chroma, Inventec,
 *   Aetina, Kikawa, Lotus Pharma, iKala, WINBRO, Quaser, Ørsted
 *
 * case anonym map:
 *   edge-ai-storage      = Innodisk
 *   fluid-equipment      = Kikawa
 *   test-measurement     = Chroma
 *   global-semiconductor = MediaTek
 *
 * testimonial authors (names withheld pending consent):
 *   edge-ai-storage  → Randy Chien, VP of Marketing, Innodisk
 *   fluid-equipment  → Sarah Chen, Brand Director, Kikawa
 *
 * former gap tags:
 *   tech-humanity      → Innodisk, MediaTek, Aetina
 *   data-story         → Chroma, WINBRO
 *   legacy-future      → Kikawa, Inventec, Quaser
 *   local-global       → Lotus Pharma, Ørsted
 *   perception-reality → MediaTek, Advantech
 * ---------------------------------------------------------------------------
 */
